'use client';

import { c as _c } from "react/compiler-runtime";
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import type { MPAInterceptedResponse, ActionApprovalRule, MPARequest } from '@repo/api-gw-sdk';
import { MPAEntityType, MPAOperationScope, MPAOperationType } from '@repo/api-gw-sdk';
import { Fragment, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { MpaRequestDetails, useMpaRequestDetailsView } from '@/components/mpa/MpaRequestDetailsDialog';
import { operationVerbs, toMPAOperation } from '@/components/mpa/constants';
import { Icon } from '@/components/shared/icon';
import { useDAL } from '@/contexts/useDAL';
import { MpaOperations } from '@/data/inventory/data';
import { pluralize } from '@/utils/string';
import { BackupPolicyEntity, MPARequestResourceEntities, RestoreAccountEntity, getBackupPolicyId, getBackupPolicyNameFromState } from './mpaRequestEntity';
type Pending = 'approve' | 'cancel' | null;
const formatHours = (hours: number) => `${hours} ${pluralize('hour', hours)}`;
const formatApprovers = (n: number) => `${n} ${pluralize('approver', n)}`;
export function MpaInterceptDialogContent({
  payload,
  onApprove,
  onCancel,
  showDetails = false,
  onShowDetails
}: {
  payload: MPAInterceptedResponse;
  onApprove: (comment: string) => Promise<void>;
  onCancel: () => Promise<void>;
  showDetails?: boolean;
  onShowDetails?: () => void;
}) {
  const dal = useDAL();
  const {
    data: operationsBody
  } = dal.mpa.operations.useList();
  const [pending, setPending] = useState<Pending>(null);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const request = payload.actionApprovalRequest;
  const policies = payload.mpaPolicies?.policies ?? [];
  const actionApprovalOperation = policies[0]?.operation;
  const operation = actionApprovalOperation ? toMPAOperation(actionApprovalOperation) : undefined;
  const operationName = operation ? MpaOperations[operation]?.name ?? '' : '';
  const scope = operation ? operationsBody?.operations.find(o => o.operation === operation)?.scope : undefined;
  const isBackupPolicyScope = request?.entityType === MPAEntityType.MPA_ENTITY_TYPE_BACKUP_POLICY || scope === MPAOperationScope.MPA_OPERATION_SCOPE_BACKUP_POLICY;
  const isCloudResourceScope = request?.entityType === MPAEntityType.MPA_ENTITY_TYPE_CLOUD_RESOURCE || scope === MPAOperationScope.MPA_OPERATION_SCOPE_CLOUD_RESOURCE;
  const isSnapshotScope = request?.entityType === MPAEntityType.MPA_ENTITY_TYPE_SNAPSHOT || scope === MPAOperationScope.MPA_OPERATION_SCOPE_SNAPSHOT;
  const isAddRestoreAccount = operation === MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_ADD_RESTORE_ACCOUNT;
  const run = (kind: Exclude<Pending, null>, action: () => Promise<void>) => async () => {
    setError(null);
    setPending(kind);
    try {
      await action();
    } catch (e) {
      setError((e as Error).message);
      setPending(null);
    }
  };
  const handleApprove = run('approve', () => onApprove(comment));
  const handleCancel = run('cancel', onCancel);
  const disabled = pending !== null;
  return <Stack data-testid='mpa-intercept-dialog' className='h-full' sx={{
    overflow: 'hidden'
  }}>
      <Stack sx={{
      flex: 1,
      minHeight: 0,
      position: 'relative'
    }}>
        <Stack sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        visibility: showDetails ? 'hidden' : 'visible'
      }}>
          <Stack sx={{
          alignItems: 'center',
          gap: '24px',
          flexShrink: 0
        }}>
            <Stack sx={{
            alignItems: 'center',
            gap: '24px',
            width: '480px',
            textAlign: 'center',
            pt: '8px'
          }}>
              <Icon iconClass='material-symbols-approval-outline' />
              <Typography variant='h2'>Approval required</Typography>
              <Typography variant='body1' sx={{
              color: 'text.secondary'
            }}>
                This action requires approval. Once your request is approved,
                you&apos;ll have a limited time to apply it yourself.
              </Typography>
            </Stack>

            <Stack sx={{
            width: '582px'
          }}>
              <TextField fullWidth multiline minRows={1} placeholder='Tell approvers why this action is needed (optional)' value={comment} onChange={e_0 => setComment(e_0.target.value)} disabled={disabled} sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0 100px 100px 64px',
                padding: '12px 16px',
                fontSize: '13px',
                lineHeight: '20px'
              },
              '& .MuiOutlinedInput-input': {
                padding: 0
              }
            }} />
            </Stack>
          </Stack>

          <Stack sx={{
          alignItems: 'center',
          py: '24px'
        }}>
            <Stack sx={{
            width: '582px',
            pb: '24px'
          }}>
              <Stack direction='row' sx={{
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: '12px',
              backgroundColor: 'var(--mui-palette-background-secondary)',
              borderRadius: '8px',
              padding: '24px 24px 24px 40px'
            }}>
                <Typography variant='subtitle1'>
                  {`You're requesting to ${operation ? operationVerbs[operation] : 'perform'}`}
                </Typography>
                {isBackupPolicyScope ? <BackupPolicyEntity policyId={getBackupPolicyId(request)} fallbackName={getBackupPolicyNameFromState(request)} /> : isAddRestoreAccount ? <RestoreAccountEntity request={request} /> : isCloudResourceScope || isSnapshotScope ? <MPARequestResourceEntities request={request} /> : null}
                {onShowDetails && request && <ButtonWithIcon icon='material-symbols-data-object' text='View Details' onClick={onShowDetails} dataTestId='mpa-intercept-show-details' />}
              </Stack>

              {policies.map(policy => <Fragment key={policy.id}>
                  <Connector />
                  <PolicyApprovalCard policy={policy} operationName={operationName} samlGroupName={policy.approverProviderGroupId ?? undefined} />
                </Fragment>)}

              {error && <Typography variant='body2' sx={{
              color: 'error.main',
              pt: '16px'
            }}>
                  {error}
                </Typography>}
            </Stack>
          </Stack>
        </Stack>

        {showDetails && request && <DetailsPanel request={request} />}
      </Stack>
      <Stack direction='row' sx={{
      gap: '12px',
      justifyContent: 'flex-end',
      alignItems: 'center',
      flexShrink: 0,
      backgroundColor: 'var(--mui-palette-background-paper)',
      boxShadow: 'var(--mui-customShadows-panelMd)',
      px: '40px',
      py: '16px'
    }}>
        <Button variant='text' onClick={() => void handleCancel()} disabled={disabled} data-testid='mpa-intercept-cancel'>
          Cancel Action
        </Button>
        <Button variant='contained' onClick={() => void handleApprove()} disabled={disabled} data-testid='mpa-intercept-approve'>
          Request Approval
        </Button>
      </Stack>
    </Stack>;
}
const DetailsPanel = t0 => {
  const $ = _c(4);
  const {
    request
  } = t0;
  const {
    view
  } = useMpaRequestDetailsView(request);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      position: "absolute",
      inset: "0 0 5px 0",
      backgroundColor: "var(--mui-palette-background-paper)",
      overflow: "auto",
      px: "40px",
      py: "24px",
      gap: "16px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== request || $[2] !== view) {
    t2 = <Stack sx={t1}><MpaRequestDetails request={request} view={view} /></Stack>;
    $[1] = request;
    $[2] = view;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const Connector = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Box sx={{
      width: "1px",
      height: "24px",
      ml: "40px",
      backgroundColor: "var(--mui-palette-divider)"
    }} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const PolicyApprovalCard = t0 => {
  const $ = _c(39);
  const {
    policy,
    operationName,
    samlGroupName
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: "24px",
      backgroundColor: "var(--mui-palette-background-secondary)",
      borderRadius: "8px",
      pt: "40px",
      pb: "12px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      gap: "24px",
      px: "40px"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== operationName) {
    t3 = <Typography variant="body1">{"For "}<Typography component="span" variant="h4">{operationName},{" "}</Typography>{"those approval steps are required:"}</Typography>;
    $[2] = operationName;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      gap: "24px",
      pb: "24px"
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== policy.requiredApprovals) {
    t5 = formatApprovers(policy.requiredApprovals);
    $[5] = policy.requiredApprovals;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== samlGroupName) {
    t6 = samlGroupName ? <><Typography variant="subtitle1">in</Typography><Icon iconClass="material-symbols-group-outline" /><Typography variant="h4">{samlGroupName}</Typography></> : undefined;
    $[7] = samlGroupName;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <ApprovalStepRow label="From" value={t5} endAdornment={t6} />;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== policy.approvalWindowHours) {
    t8 = formatHours(policy.approvalWindowHours);
    $[12] = policy.approvalWindowHours;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <ApprovalStepRow label="Within" value={t8} />;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t7 || $[17] !== t9) {
    t10 = <ApprovalStep icon="material-symbols-approval-outline" title="Get approval">{t7}{t9}</ApprovalStep>;
    $[16] = t7;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== policy.executionWindowHours) {
    t11 = formatHours(policy.executionWindowHours);
    $[19] = policy.executionWindowHours;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11) {
    t12 = <ApprovalStep icon="material-symbols-ads-click" title="Apply the action" isLast={true}><ApprovalStepRow label="Within" value={t11} suffix="after approval" /></ApprovalStep>;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t12) {
    t13 = <Stack sx={t4}>{t10}{t12}</Stack>;
    $[23] = t10;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13 || $[27] !== t3) {
    t14 = <Stack sx={t2}>{t3}{t13}</Stack>;
    $[26] = t13;
    $[27] = t3;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Box sx={{
      height: "1px",
      backgroundColor: "var(--mui-palette-divider)",
      width: "100%"
    }} />;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      px: "40px"
    };
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <AccordionSummary><Typography variant="h4">Description</Typography></AccordionSummary>;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      p: 0,
      pl: "36px",
      pb: "8px"
    };
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      color: "text.secondary",
      whiteSpace: "pre-wrap"
    };
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const t20 = policy.description || "--";
  let t21;
  if ($[34] !== t20) {
    t21 = <Accordion sx={t16}>{t17}<AccordionDetails sx={t18}><Typography variant="body1" sx={t19}>{t20}</Typography></AccordionDetails></Accordion>;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== t14 || $[37] !== t21) {
    t22 = <Stack sx={t1}>{t14}{t15}{t21}</Stack>;
    $[36] = t14;
    $[37] = t21;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  return t22;
};
const ApprovalStep = t0 => {
  const $ = _c(22);
  const {
    icon,
    title,
    isLast,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: "24px",
      alignItems: "stretch"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      alignItems: "center",
      gap: "16px",
      alignSelf: "stretch"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== icon) {
    t3 = <Icon iconClass={icon} color="var(--mui-palette-text-disabled)" />;
    $[2] = icon;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== isLast) {
    t4 = !isLast && <Box sx={{
      flex: 1,
      minHeight: "24px",
      width: 0,
      borderLeft: "1px dashed var(--mui-palette-divider)"
    }} />;
    $[4] = isLast;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <Stack sx={t2}>{t3}{t4}</Stack>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      gap: "16px",
      paddingTop: "4px",
      flex: 1,
      minWidth: 0
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      color: "text.disabled"
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== title) {
    t8 = <Typography variant="h3" sx={t7}>{title}</Typography>;
    $[11] = title;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      gap: "12px"
    };
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== children) {
    t10 = <Stack sx={t9}>{children}</Stack>;
    $[14] = children;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t8) {
    t11 = <Stack sx={t6}>{t8}{t10}</Stack>;
    $[16] = t10;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t5) {
    t12 = <Stack direction="row" sx={t1}>{t5}{t11}</Stack>;
    $[19] = t11;
    $[20] = t5;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
};
const ApprovalStepRow = t0 => {
  const $ = _c(13);
  const {
    label,
    value,
    suffix,
    endAdornment
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignItems: "center",
      gap: "12px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      width: "40px"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <Typography variant="subtitle1" sx={t2}>{label}</Typography>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== value) {
    t4 = <Typography variant="h4">{value}</Typography>;
    $[4] = value;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== suffix) {
    t5 = suffix && <Typography variant="subtitle1">{suffix}</Typography>;
    $[6] = suffix;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== endAdornment || $[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t6 = <Stack direction="row" sx={t1}>{t3}{t4}{endAdornment}{t5}</Stack>;
    $[8] = endAdornment;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};